import { Head, Link, router } from "@inertiajs/react"
import { CheckCircle2, Copy, Sparkles } from "lucide-react"
import { useState } from "react"
import { LandingFooter } from "@/components/landing/footer"
import { LandingHeader } from "@/components/landing/header"

interface DashboardProps {
  user: {
    email: string
    googleAdsConnected: boolean
    managerCustomerId: string | null
  }
  mcpUrl: string
  currentSessionId: string
}

const examplePrompts = [
  "List my Google Ads accounts.",
  "Check my ads — is anything wasting money this week?",
  "Pull the search terms report for my main campaign and surface negative-keyword candidates.",
  "Pause the Brand Awareness campaign over the weekend.",
]

export default function Dashboard({
  user,
  mcpUrl,
  currentSessionId,
}: DashboardProps) {
  const installCommand = `claude mcp add --transport http adwizard ${mcpUrl}`
  const [copied, setCopied] = useState(false)

  const copyCommand = async () => {
    await navigator.clipboard.writeText(installCommand)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const signOut = () => {
    router.delete(`/sessions/${currentSessionId}`)
  }

  return (
    <>
      <Head title="Dashboard" />
      <div className="flex min-h-screen flex-col bg-background">
        <LandingHeader signedIn onSignOut={signOut} />

        <main className="mx-auto w-full max-w-4xl flex-1 px-6 py-12">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            You're connected
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-foreground">
            Welcome to AdWizard.
          </h1>
          <p className="mt-3 text-muted-foreground">
            Signed in as <span className="text-foreground">{user.email}</span>.
          </p>

          {user.googleAdsConnected && (
            <div className="mt-6 flex items-start gap-3 rounded-lg border border-border bg-card p-4 text-sm">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
              <div>
                <div className="font-medium text-foreground">
                  Google Ads connected
                </div>
                <div className="mt-1 text-muted-foreground">
                  {user.managerCustomerId
                    ? `Manager account ${user.managerCustomerId} detected. AdWizard can now read and act on every account linked to it.`
                    : "AdWizard will use the Google Ads accounts your Google login has access to."}
                </div>
              </div>
            </div>
          )}

          <section className="mt-12">
            <h2 className="text-2xl font-semibold text-foreground">
              1. Add AdWizard to Claude Code
            </h2>
            <p className="mt-2 text-muted-foreground">
              Run this command in your terminal. Claude Code will register
              AdWizard as an MCP server.
            </p>
            <div className="mt-4 overflow-hidden rounded-lg border border-border bg-secondary">
              <div className="flex items-center justify-between border-b border-border bg-card px-4 py-2 text-xs text-muted-foreground">
                <span>Terminal</span>
                <button
                  type="button"
                  onClick={copyCommand}
                  className="inline-flex items-center gap-1.5 rounded px-2 py-1 hover:bg-secondary hover:text-foreground"
                >
                  <Copy className="h-3.5 w-3.5" />
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <pre className="overflow-x-auto px-4 py-3 font-mono text-sm text-secondary-foreground">
                <code>{installCommand}</code>
              </pre>
            </div>
          </section>

          <section className="mt-12">
            <h2 className="text-2xl font-semibold text-foreground">
              2. Authorize Claude
            </h2>
            <p className="mt-2 text-muted-foreground">
              The first time Claude calls an AdWizard tool, your browser will
              open and ask you to authorize the connection. Sign in (you
              already have a session here) and click <strong>Authorize</strong>.
              Claude gets a token and you don't have to do this again.
            </p>
          </section>

          <section className="mt-12">
            <h2 className="text-2xl font-semibold text-foreground">
              3. Just ask
            </h2>
            <p className="mt-2 text-muted-foreground">
              Open Claude Code and try one of these:
            </p>
            <ul className="mt-4 space-y-2">
              {examplePrompts.map((prompt) => (
                <li
                  key={prompt}
                  className="rounded-md border border-border bg-card px-4 py-3 text-sm text-foreground"
                >
                  "{prompt}"
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-12 rounded-lg border border-border bg-card p-6">
            <h3 className="text-lg font-semibold text-foreground">
              What can Claude do?
            </h3>
            <ul className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
              <li>List accounts and campaigns</li>
              <li>Read campaign and keyword performance</li>
              <li>Pull search terms reports</li>
              <li>Add campaign-level negative keywords</li>
              <li>Update daily budgets</li>
              <li>Edit Responsive Search Ad copy</li>
              <li>Pause and enable campaigns</li>
              <li>Create new Search campaigns (in PAUSED state)</li>
            </ul>
          </section>

          <section className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6 text-sm text-muted-foreground">
            <div>
              Need to revoke access?{" "}
              <a
                href="https://myaccount.google.com/permissions"
                target="_blank"
                rel="noreferrer"
                className="text-foreground underline"
              >
                Manage permissions in your Google account
              </a>
              .
            </div>
            <div className="flex items-center gap-4">
              <Link href="/sessions" className="hover:text-foreground">
                Manage sessions
              </Link>
              <button
                type="button"
                onClick={signOut}
                className="rounded-md border border-border px-3 py-1.5 text-foreground hover:bg-card"
              >
                Sign out
              </button>
            </div>
          </section>
        </main>

        <LandingFooter />
      </div>
    </>
  )
}
